import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { usePageTracking } from '../tracking';
import { FormBar, FormToast, SignatureBoxes, SubmitHotspots, loadFormFields, hasFilledValues, saveFilledCopy, useFormSubmit, DEFAULT_SIGN_CONSENT } from './pdfForm';
import { onPdfLoadError } from './ReloadNotice';
import { PDF_DOCUMENT_OPTIONS } from '../pdfOptions';
import { pdfWorkerSrc } from '../publicPath';

// The signature dialog (and its handwriting font) only loads when someone signs.
const SignaturePad = lazy(() => import('./SignaturePad'));

// Self-hosted worker, copied from node_modules at build time by the `copy-pdf-worker`
// npm script. Dev: served from /pdf.worker.min.js. Production: ABSOLUTE URL on the
// main bundle's host so that custom domains, which don't serve /static/ themselves,
// still load it. Caddy must send CORS headers on /static/*
// for pdfjs's cross-origin worker blob trick to work.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

// Fixed scale at which every page is rasterized by pdfjs. All zoom changes up
// to this value are pure CSS transforms — zero async re-render, zero commit flash.
// Above this we'd need to re-render for crispness; 1.5 is a good balance between
// initial-render speed and sharp appearance at typical display scales (fit-page
// for A4 on desktop lands at ~150%, which exactly matches RENDER_SCALE here).
// Retina devices multiply by devicePixelRatio internally, so effective pixel
// count is already ~3x at this setting.
const RENDER_SCALE = 1.5;

const MIN_SCALE = 0.25;
const MAX_SCALE = 5;
const WHEEL_ZOOM_SENSITIVITY = 400;
const BUTTON_ZOOM_STEP = 1.2;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_DIST_PX = 30;

// Touch devices — iOS Safari especially — have a hard per-tab memory ceiling and
// a per-canvas pixel cap. Big PDFs blow past both: pdfjs multiplies RENDER_SCALE
// by the real devicePixelRatio (2-3 on Retina), so each page canvas is ~50-75MB,
// and the rendered set used to grow without bound as you scrolled. Both together
// get the tab killed on device (and "struggle" on the roomier simulator).
const IS_TOUCH =
    typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
// Cap rasterization at 2x DPR on touch (iPhones are 3x). Backing store stays at
// RENDER_SCALE * 2 = 3x CSS pixels, so pages remain crisp all the way to the CSS-
// zoom ceiling — smoothness AND sharpness are preserved — while cutting canvas
// memory ~56% vs native 3x. Desktop keeps full Retina (undefined => pdfjs uses
// window.devicePixelRatio).
const CANVAS_DPR =
    IS_TOUCH && typeof window !== 'undefined'
        ? Math.min(window.devicePixelRatio || 1, 2)
        : undefined;
// Pages pre-rendered on each side of a visible page.
const RENDER_BUFFER = IS_TOUCH ? 1 : 2;
// Pages kept rendered on each side of the current page before they're evicted
// back to placeholders. Must exceed RENDER_BUFFER's reach so we don't thrash with
// the IntersectionObserver. Bounds total canvas memory regardless of doc length.
const KEEP_WINDOW = IS_TOUCH ? 3 : 6;

const PaperReactPdf = ({
    documentUrl,
    dlEnabled,
    printEnabled,
    showBranding,
    brandingLink,
    brandLogo,
    brandColor,
    cta,
    reportUrl,
    tracking,
    formConfig,
    title,
}) => {
    const [numPages, setNumPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const recordClick = usePageTracking({ tracking, currentPage, numPages });
    const [pageInput, setPageInput] = useState('1');
    // displayScale drives the UI readout and the initial/committed transform.
    // During gestures we update the DOM imperatively via refs without touching state.
    const [displayScale, setDisplayScale] = useState(1);
    // Page dimensions from the first loaded page — used for placeholder sizes so the
    // scrollable height is stable before pages actually render.
    const [firstPageDims, setFirstPageDims] = useState(null);
    // Set of page indices (0-based) that are currently rendered via <Page>. Others show
    // a same-sized placeholder. Starts with the first few so there's something to see
    // before IntersectionObserver fires.
    // Only page 0 is rendered initially; the IntersectionObserver adds neighbours
    // as they come near the viewport. Keeping this to 1 gets first paint of page 1
    // dramatically faster on heavy docs.
    const [renderedPages, setRenderedPages] = useState(() => new Set([0]));

    // Search state
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchMatches, setSearchMatches] = useState([]); // { pageIdx, occurrenceInPage }[]
    const [currentMatchIdx, setCurrentMatchIdx] = useState(-1);
    // Flat text per page, populated lazily on first search so document load isn't
    // blocked extracting text from every page in the pdfjs worker.
    const pageTextsRef = useRef([]);
    const pdfProxyRef = useRef(null);
    // Fillable PDF (AcroForm): the fields, once read; null for ordinary PDFs.
    const [form, setForm] = useState(null);
    // Signature fields: {field name: {type, image, signer_name, method}}, and
    // the field whose sign pad is open.
    const [signed, setSigned] = useState({});
    const [signingField, setSigningField] = useState(null);
    const textExtractionPromiseRef = useRef(null);
    const [textIndexing, setTextIndexing] = useState(false);
    const [loadProgress, setLoadProgress] = useState(null); // { loaded, total } | null
    const searchInputRef = useRef(null);

    const scaleRef = useRef(1);
    const rootContainerRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const transformTargetRef = useRef(null);

    const wheelPendingRef = useRef(null);
    const wheelTimeoutRef = useRef(null);
    const scrollRafRef = useRef(null);

    const pinchActiveRef = useRef(false);
    const pinchInitialDistanceRef = useRef(0);
    const pinchMidpointRef = useRef({ x: 0, y: 0 });
    const pinchStartScaleRef = useRef(1);
    const pinchRafRef = useRef(null);
    const lastTapRef = useRef({ time: 0, x: 0, y: 0 });

    const clampScale = useCallback(
        (s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s)),
        []
    );

    // Apply current scale + origin to the wrapper imperatively. Called during
    // gestures, on commit, and once after first mount. React reconciliation
    // will not fight us because the JSX style prop never includes `transform`.
    // Apply zoom: sets CSS zoom (scales layout + visuals together) and anchors scroll
    // so the same content point stays under the anchor cursor/finger.
    const applyZoom = useCallback(
        (newScale, anchorX, anchorY) => {
            const container = scrollContainerRef.current;
            const target = transformTargetRef.current;
            if (!container || !target) return;

            const oldScale = scaleRef.current;
            const clamped = clampScale(newScale);
            if (Math.abs(clamped - oldScale) < 0.0001) return;

            const rect = container.getBoundingClientRect();
            const ax = anchorX == null ? rect.left + rect.width / 2 : anchorX;
            const ay = anchorY == null ? rect.top + rect.height / 2 : anchorY;
            const relX = ax - rect.left;
            const relY = ay - rect.top;
            // Anchor's position in the element's current layout coord system
            const contentX = container.scrollLeft + relX;
            const contentY = container.scrollTop + relY;

            scaleRef.current = clamped;

            // Set zoom — this resizes both layout and visuals. No transform needed.
            const cssZoom = clamped / RENDER_SCALE;
            target.style.zoom = cssZoom;
            target.style.opacity = '1';

            // After zoom, layout coords scaled by ratio. Adjust scroll so that the content point
            // that was at the anchor cursor stays at the anchor cursor.
            const ratio = clamped / oldScale;
            container.scrollLeft = contentX * ratio - relX;
            container.scrollTop = contentY * ratio - relY;
        },
        [clampScale]
    );

    // Public setter used by buttons / double-tap. Takes same args as applyZoom, plus
    // updates React state so the UI readout updates.
    const setScaleImmediate = useCallback(
        (newScale, anchorX, anchorY) => {
            applyZoom(newScale, anchorX, anchorY);
            setDisplayScale(scaleRef.current);
        },
        [applyZoom]
    );

    // Navigate to a specific page (1-indexed). Scrolls the container so the target
    // page is at the top of the visible area (minus a small gap).
    const goToPage = useCallback((pageNum) => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const pages = container.querySelectorAll('.react-pdf__Page');
        if (!pages || pages.length === 0) return;
        const idx = Math.max(0, Math.min(pages.length - 1, pageNum - 1));
        const target = pages[idx];
        const targetRect = target.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const delta = targetRect.top - containerRect.top - 20;
        container.scrollTo({
            top: container.scrollTop + delta,
            behavior: 'smooth',
        });
    }, []);

    // Bring a form field into view and focus it (a required one left empty).
    // Its page may still be a placeholder, so scroll there and wait for the
    // annotation layer to render the input.
    const focusField = useCallback((field) => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const wrap = container.querySelector(`[data-page-index="${field.page}"]`);
        if (wrap) wrap.scrollIntoView({ block: 'start' });
        const id = field.widgets[0].id;
        let tries = 0;
        const attempt = () => {
            if (field.kind === 'signature') {
                const box = container.querySelector(`[data-sig-widget="${id}"]`);
                if (box) {
                    box.scrollIntoView({ block: 'center' });
                    box.focus({ preventScroll: true });
                    box.classList.remove('pdfdeck-sig-missing');
                    void box.offsetWidth; // restart the pulse
                    box.classList.add('pdfdeck-sig-missing');
                    setTimeout(() => box.classList.remove('pdfdeck-sig-missing'), 4000);
                    return;
                }
                if (tries++ < 50) setTimeout(attempt, 100);
                return;
            }
            const el = container.querySelector(`[data-element-id="${id}"]`);
            if (el) {
                el.scrollIntoView({ block: 'center' });
                if (el.focus) el.focus({ preventScroll: true });
                const section = el.closest('section') || el;
                section.classList.add('pdfdeck-field-missing');
                setTimeout(() => section.classList.remove('pdfdeck-field-missing'), 4000);
                return;
            }
            if (tries++ < 50) setTimeout(attempt, 100);
        };
        attempt();
    }, []);

    // Fillable PDF submissions (pdfForm.js). Off unless the owner turned on
    // "Enable submissions": then fields are still fillable, there's no Submit
    // and no signing, and the toolbar download saves the filled copy.
    const formCfg = formConfig || {};
    const formsOn = Boolean(form) && !formCfg.off;
    const onFormSubmitted = useCallback(() => recordClick({ text: 'Form submitted', href: '' }), [recordClick]);
    const formSub = useFormSubmit({
        pdf: pdfProxyRef.current,
        form,
        config: formCfg,
        title,
        signed,
        onMissing: focusField,
        onSubmitted: onFormSubmitted,
    });

    const pagePrev = useCallback(() => {
        goToPage(currentPage - 1);
    }, [goToPage, currentPage]);

    const pageNext = useCallback(() => {
        goToPage(currentPage + 1);
    }, [goToPage, currentPage]);

    const fitToPage = useCallback(() => {
        const container = scrollContainerRef.current;
        const pages = container?.querySelectorAll('.react-pdf__Page');
        if (!container || !pages || pages.length === 0) return;
        const page = pages[0];
        const rect = page.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;
        const natW = rect.width / scaleRef.current;
        const natH = rect.height / scaleRef.current;
        const cw = container.clientWidth - 40;
        const ch = container.clientHeight - 40;
        setScaleImmediate(Math.min(cw / natW, ch / natH));
    }, [setScaleImmediate]);

    const fitToWidth = useCallback(() => {
        const container = scrollContainerRef.current;
        const pages = container?.querySelectorAll('.react-pdf__Page');
        if (!container || !pages || pages.length === 0) return;
        const page = pages[0];
        const rect = page.getBoundingClientRect();
        if (rect.width === 0) return;
        const natW = rect.width / scaleRef.current;
        setScaleImmediate((container.clientWidth - 40) / natW);
    }, [setScaleImmediate]);

    const toggleFullscreen = useCallback(() => {
        const container = rootContainerRef.current;
        if (!container) return;
        if (document.fullscreenElement) {
            document.exitFullscreen?.();
        } else {
            container.requestFullscreen?.();
        }
    }, []);

    const handleDocumentLoad = useCallback((pdf) => {
        pdfProxyRef.current = pdf;
        setNumPages(pdf.numPages);
        loadFormFields(pdf, {
            submitButton: (formConfig && formConfig.submitButton) || '',
            labels: (formConfig && formConfig.labels) || {},
        }).then((fields) => {
            if (pdfProxyRef.current === pdf) setForm(fields);
        });
        pdf.getPage(1).then((page) => {
            const viewport = page.getViewport({ scale: 1 });
            setFirstPageDims({ width: viewport.width, height: viewport.height });
            const container = scrollContainerRef.current;
            if (!container) return;

            // Dynamic default scale:
            //   Landscape PDFs → fit-page (show the whole slide).
            //   Portrait PDFs → fit-width on narrow screens, capped page-width on wide
            //     screens (so A4 etc. renders at a comfortable reading size on big monitors
            //     instead of a small rectangle with lots of whitespace on the sides).
            const PADDING_PX = 24;
            const MAX_PORTRAIT_PAGE_WIDTH_PX = 900;
            const cw = container.clientWidth - PADDING_PX;
            const ch = container.clientHeight - PADDING_PX;
            const fitWidth = cw / viewport.width;
            const fitHeight = ch / viewport.height;
            const fitPage = Math.min(fitWidth, fitHeight);
            const pageAspect = viewport.width / viewport.height;

            let initialScale;
            if (pageAspect >= 1.0) {
                // Landscape or near-square → whole-page view
                initialScale = fitPage;
            } else {
                // Portrait → cap effective page width at the constant on big screens
                const effectiveWidthPx = Math.min(
                    cw,
                    MAX_PORTRAIT_PAGE_WIDTH_PX
                );
                initialScale = clampScale(
                    effectiveWidthPx / viewport.width
                );
            }

            requestAnimationFrame(() => {
                const target = transformTargetRef.current;
                if (!target) return;
                scaleRef.current = initialScale;
                target.style.zoom = initialScale / RENDER_SCALE;
                target.style.opacity = '1';
                setDisplayScale(initialScale);
            });
        });

        // Schedule text extraction for browser-idle time so it doesn't block initial
        // page rendering. By the time the user opens search, the index is usually ready.
        // Falls back to a setTimeout on browsers without requestIdleCallback (Safari).
        const schedule =
            typeof window !== 'undefined' && window.requestIdleCallback
                ? (cb) => window.requestIdleCallback(cb, { timeout: 3000 })
                : (cb) => setTimeout(cb, 1500);
        schedule(() => {
            // ensureTextExtracted is defined below; accessing via closure on call is fine
            // because this fires asynchronously after render.
            ensureTextExtracted();
        });
    }, []);

    // Kick off text extraction on demand (first search) so document load isn't blocked.
    const ensureTextExtracted = useCallback(() => {
        if (textExtractionPromiseRef.current) {
            return textExtractionPromiseRef.current;
        }
        const pdf = pdfProxyRef.current;
        if (!pdf) return Promise.resolve();
        setTextIndexing(true);
        const promise = Promise.all(
            Array.from({ length: pdf.numPages }, (_, i) =>
                pdf
                    .getPage(i + 1)
                    .then((p) => p.getTextContent())
                    .then((content) =>
                        content.items.map((it) => it.str).join(' ')
                    )
                    .catch(() => '')
            )
        ).then((texts) => {
            pageTextsRef.current = texts;
            setTextIndexing(false);
        });
        textExtractionPromiseRef.current = promise;
        return promise;
    }, []);

    // Pure synchronous search against pre-extracted page texts.
    const runSearchInternal = useCallback((query) => {
        const q = (query || '').trim();
        if (q.length < 1) {
            setSearchMatches([]);
            setCurrentMatchIdx(-1);
            return;
        }
        const lowerQ = q.toLowerCase();
        const matches = [];
        pageTextsRef.current.forEach((text, pageIdx) => {
            if (!text) return;
            const lower = text.toLowerCase();
            let idx = 0;
            let occurrence = 0;
            while (true) {
                const found = lower.indexOf(lowerQ, idx);
                if (found === -1) break;
                matches.push({ pageIdx, occurrenceInPage: occurrence });
                idx = found + lowerQ.length;
                occurrence += 1;
            }
        });
        setSearchMatches(matches);
        setCurrentMatchIdx(matches.length > 0 ? 0 : -1);
    }, []);

    // Public search: triggers lazy text extraction on first call.
    const runSearch = useCallback(
        async (query) => {
            if (pageTextsRef.current.length === 0) {
                await ensureTextExtracted();
            }
            runSearchInternal(query);
        },
        [ensureTextExtracted, runSearchInternal]
    );

    const goToSearchMatch = useCallback(
        (idx) => {
            if (idx < 0 || idx >= searchMatches.length) return;
            setCurrentMatchIdx(idx);
            const match = searchMatches[idx];
            goToPage(match.pageIdx + 1);
        },
        [searchMatches, goToPage]
    );

    const closeSearch = useCallback(() => {
        setSearchOpen(false);
        setSearchQuery('');
        setSearchMatches([]);
        setCurrentMatchIdx(-1);
    }, []);

    // Virtualization: observe page wrappers, render <Page> only for visible + buffer.
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || numPages === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                setRenderedPages((prev) => {
                    const next = new Set(prev);
                    let changed = false;
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) return;
                        const idx = parseInt(
                            entry.target.getAttribute('data-page-index'),
                            10
                        );
                        if (Number.isNaN(idx)) return;
                        const from = Math.max(0, idx - RENDER_BUFFER);
                        const to = Math.min(numPages - 1, idx + RENDER_BUFFER);
                        for (let i = from; i <= to; i++) {
                            if (!next.has(i)) {
                                next.add(i);
                                changed = true;
                            }
                        }
                    });
                    return changed ? next : prev;
                });
            },
            {
                root: container,
                rootMargin: '500px 0px 500px 0px',
            }
        );

        const wrappers = container.querySelectorAll('[data-page-index]');
        wrappers.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [numPages]);

    // Evict pages that have drifted far from the current page back to placeholders.
    // The observer only ever *adds* pages, so without this the rendered set grows
    // monotonically and a long doc exhausts mobile memory (iOS kills the tab).
    // KEEP_WINDOW exceeds the observer's reach, so on-screen pages are never evicted
    // and there's no add/evict thrash. Pages re-render from placeholder on scroll-back.
    useEffect(() => {
        setRenderedPages((prev) => {
            const center = currentPage - 1; // currentPage is 1-based, indices 0-based
            const lo = center - KEEP_WINDOW;
            const hi = center + KEEP_WINDOW;
            let changed = false;
            const next = new Set();
            prev.forEach((i) => {
                if (i >= lo && i <= hi) next.add(i);
                else changed = true;
            });
            return changed ? next : prev;
        });
    }, [currentPage]);

    // Keep currentPage in sync with whichever page is most visible in the viewport.
    // Depends on firstPageDims too so we re-run once wrappers have real heights.
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || numPages === 0) return;

        const detect = () => {
            scrollRafRef.current = null;
            const wrappers = container.querySelectorAll('[data-page-index]');
            if (wrappers.length === 0) return;
            // Skip detection until wrappers have real dimensions. Before firstPageDims
            // is set, placeholders are 0-height and stacking order gives false results.
            const anyReady = Array.from(wrappers).some(
                (w) => w.getBoundingClientRect().height > 50
            );
            if (!anyReady) return;

            const containerRect = container.getBoundingClientRect();
            const viewportCenterY = containerRect.top + containerRect.height / 2;
            let closest = 1;
            let closestDist = Infinity;
            wrappers.forEach((wrapper) => {
                const idx = parseInt(
                    wrapper.getAttribute('data-page-index'),
                    10
                );
                if (Number.isNaN(idx)) return;
                const r = wrapper.getBoundingClientRect();
                // Ignore wrappers that haven't been laid out yet (placeholders
                // before firstPageDims resolves).
                if (r.height < 10) return;
                const pageCenterY = r.top + r.height / 2;
                const dist = Math.abs(pageCenterY - viewportCenterY);
                if (dist < closestDist) {
                    closestDist = dist;
                    closest = idx + 1;
                }
            });
            setCurrentPage((prev) => {
                if (prev !== closest) {
                    setPageInput(String(closest));
                    return closest;
                }
                return prev;
            });
        };

        const handleScroll = () => {
            if (scrollRafRef.current !== null) return;
            scrollRafRef.current = requestAnimationFrame(detect);
        };

        container.addEventListener('scroll', handleScroll, { passive: true });
        // Initial detect after mount
        detect();
        return () => {
            container.removeEventListener('scroll', handleScroll);
            if (scrollRafRef.current !== null) {
                cancelAnimationFrame(scrollRafRef.current);
                scrollRafRef.current = null;
            }
        };
    }, [numPages, firstPageDims]);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKey = (e) => {
            const tag = (e.target && e.target.tagName) || '';
            const isMod = e.metaKey || e.ctrlKey;

            // Cmd/Ctrl+F opens search (works everywhere, including inside inputs)
            if (isMod && (e.key === 'f' || e.key === 'F')) {
                e.preventDefault();
                setSearchOpen(true);
                setTimeout(() => searchInputRef.current?.focus(), 0);
                return;
            }

            // Escape closes search if open, else exits fullscreen
            if (e.key === 'Escape') {
                if (searchOpen) {
                    e.preventDefault();
                    closeSearch();
                    return;
                }
            }

            // Don't steal keys from inputs (incl. PDF form fields) for anything else
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

            if (!isMod) {
                if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
                    e.preventDefault();
                    pagePrev();
                } else if (
                    e.key === 'ArrowRight' ||
                    e.key === 'PageDown' ||
                    e.key === ' '
                ) {
                    e.preventDefault();
                    pageNext();
                } else if (e.key === 'Home') {
                    e.preventDefault();
                    goToPage(1);
                } else if (e.key === 'End') {
                    e.preventDefault();
                    goToPage(numPages);
                } else if (e.key === 'f' || e.key === 'F') {
                    toggleFullscreen();
                }
            } else {
                if (e.key === '=' || e.key === '+') {
                    e.preventDefault();
                    setScaleImmediate(scaleRef.current * BUTTON_ZOOM_STEP);
                } else if (e.key === '-') {
                    e.preventDefault();
                    setScaleImmediate(scaleRef.current / BUTTON_ZOOM_STEP);
                } else if (e.key === '0') {
                    e.preventDefault();
                    fitToPage();
                }
            }
        };

        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [pagePrev, pageNext, goToPage, numPages, setScaleImmediate, fitToPage, toggleFullscreen, searchOpen, closeSearch]);

    // Search: re-highlight on rendered pages whenever query/matches/rendering changes.
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        // Clear previous highlights
        container.querySelectorAll('[data-pdfdeck-hl]').forEach((s) => {
            s.style.backgroundColor = '';
            s.style.borderRadius = '';
            s.removeAttribute('data-pdfdeck-hl');
        });

        if (!searchOpen || !searchQuery || searchMatches.length === 0) return;

        const q = searchQuery.toLowerCase();
        const currentMatch =
            currentMatchIdx >= 0 && currentMatchIdx < searchMatches.length
                ? searchMatches[currentMatchIdx]
                : null;

        const wrappers = container.querySelectorAll('[data-page-index]');
        wrappers.forEach((wrapper) => {
            const pageIdx = parseInt(
                wrapper.getAttribute('data-page-index'),
                10
            );
            if (Number.isNaN(pageIdx)) return;
            const layer = wrapper.querySelector(
                '.textLayer, .react-pdf__Page__textContent'
            );
            if (!layer) return;

            const matchingSpans = [];
            layer.querySelectorAll('span').forEach((span) => {
                const text = (span.textContent || '').toLowerCase();
                if (text.includes(q)) {
                    span.style.backgroundColor = 'rgba(255, 230, 0, 0.45)';
                    span.style.borderRadius = '2px';
                    span.setAttribute('data-pdfdeck-hl', '1');
                    matchingSpans.push(span);
                }
            });

            // Brighter highlight for the current match span and scroll it into view
            if (currentMatch && currentMatch.pageIdx === pageIdx) {
                const target = matchingSpans[currentMatch.occurrenceInPage];
                if (target) {
                    target.style.backgroundColor = 'rgba(255, 140, 0, 0.75)';
                    target.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            }
        });
    }, [searchOpen, searchQuery, searchMatches, currentMatchIdx, renderedPages]);

    // Wheel handler (trackpad pinch / ctrl-scroll) — applies zoom with scroll anchoring
    // on each tick, debounces React state sync so UI readout doesn't flood.
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;

        const handleWheel = (e) => {
            if (!e.ctrlKey && !e.metaKey) return;
            e.preventDefault();

            const factor = Math.exp(-e.deltaY / WHEEL_ZOOM_SENSITIVITY);
            applyZoom(scaleRef.current * factor, e.clientX, e.clientY);

            if (wheelTimeoutRef.current !== null) {
                clearTimeout(wheelTimeoutRef.current);
            }
            wheelTimeoutRef.current = setTimeout(() => {
                wheelTimeoutRef.current = null;
                setDisplayScale(scaleRef.current);
            }, 150);
        };

        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            if (wheelTimeoutRef.current !== null) {
                clearTimeout(wheelTimeoutRef.current);
                wheelTimeoutRef.current = null;
            }
            el.removeEventListener('wheel', handleWheel);
        };
    }, [applyZoom]);

    // Touch handlers — two-finger pinch + double-tap
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;

        const getDistance = (t1, t2) =>
            Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
        const getMidpoint = (t1, t2) => ({
            x: (t1.clientX + t2.clientX) / 2,
            y: (t1.clientY + t2.clientY) / 2,
        });

        const handleTouchStart = (e) => {
            if (e.touches.length === 2) {
                pinchActiveRef.current = true;
                pinchInitialDistanceRef.current = getDistance(
                    e.touches[0],
                    e.touches[1]
                );
                pinchMidpointRef.current = getMidpoint(
                    e.touches[0],
                    e.touches[1]
                );
                pinchStartScaleRef.current = scaleRef.current;
                lastTapRef.current = { time: 0, x: 0, y: 0 };
                e.preventDefault();
                return;
            }

            if (e.touches.length === 1) {
                // Taps on form fields are for typing, not double-tap zoom.
                const tag = (e.target && e.target.tagName) || '';
                if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
                if (e.target && e.target.closest && e.target.closest('.pdfdeck-sig-box')) return;
                const now = Date.now();
                const t = e.touches[0];
                const last = lastTapRef.current;
                const d = Math.hypot(t.clientX - last.x, t.clientY - last.y);
                if (now - last.time < DOUBLE_TAP_MS && d < DOUBLE_TAP_DIST_PX) {
                    if (scaleRef.current > 1.1) {
                        fitToPage();
                    } else {
                        setScaleImmediate(2, t.clientX, t.clientY);
                    }
                    lastTapRef.current = { time: 0, x: 0, y: 0 };
                    e.preventDefault();
                } else {
                    lastTapRef.current = {
                        time: now,
                        x: t.clientX,
                        y: t.clientY,
                    };
                }
            }
        };

        const handleTouchMove = (e) => {
            if (!pinchActiveRef.current || e.touches.length !== 2) return;
            e.preventDefault();
            const distance = getDistance(e.touches[0], e.touches[1]);
            if (pinchInitialDistanceRef.current <= 0) return;
            const rawRatio = distance / pinchInitialDistanceRef.current;
            const targetScale = clampScale(
                pinchStartScaleRef.current * rawRatio
            );
            const mid = getMidpoint(e.touches[0], e.touches[1]);

            if (pinchRafRef.current !== null) {
                cancelAnimationFrame(pinchRafRef.current);
            }
            pinchRafRef.current = requestAnimationFrame(() => {
                pinchRafRef.current = null;
                applyZoom(targetScale, mid.x, mid.y);
            });
        };

        const handleTouchEnd = (e) => {
            if (!pinchActiveRef.current) return;
            if (e.touches.length >= 2) return;
            pinchActiveRef.current = false;
            if (pinchRafRef.current !== null) {
                cancelAnimationFrame(pinchRafRef.current);
                pinchRafRef.current = null;
            }
            setDisplayScale(scaleRef.current);
        };

        const handleTouchCancel = () => {
            pinchActiveRef.current = false;
            if (pinchRafRef.current !== null) {
                cancelAnimationFrame(pinchRafRef.current);
                pinchRafRef.current = null;
            }
            setDisplayScale(scaleRef.current);
        };

        el.addEventListener('touchstart', handleTouchStart, { passive: false });
        el.addEventListener('touchmove', handleTouchMove, { passive: false });
        el.addEventListener('touchend', handleTouchEnd, { passive: false });
        el.addEventListener('touchcancel', handleTouchCancel, {
            passive: false,
        });

        return () => {
            el.removeEventListener('touchstart', handleTouchStart);
            el.removeEventListener('touchmove', handleTouchMove);
            el.removeEventListener('touchend', handleTouchEnd);
            el.removeEventListener('touchcancel', handleTouchCancel);
        };
    }, [applyZoom, clampScale, fitToPage, setScaleImmediate]);

    const isDlEnabled = dlEnabled === 'true';
    // Print is gated on download: if the uploader disabled download, print is also hidden
    // (print-to-PDF is an effective download). Otherwise print respects its own flag.
    const isPrintEnabled = isDlEnabled && printEnabled === 'true';
    // showBranding / brandingLink props are kept on the interface but not rendered
    // here — Django's pdf_viewer.html template owns the branding element.

    const handlePrint = useCallback(() => {
        if (!documentUrl) return;
        const w = window.open(documentUrl, '_blank');
        if (w) {
            w.addEventListener('load', () => {
                try { w.print(); } catch (_) { /* noop */ }
            });
        }
    }, [documentUrl]);

    const handleDownload = useCallback(async () => {
        if (!documentUrl) return;
        // Filled in a fillable PDF: download their copy, with what they typed.
        const pdf = pdfProxyRef.current;
        if (form && hasFilledValues(pdf)) {
            try {
                await saveFilledCopy(pdf, title);
                return;
            } catch (_) { /* fall back to the original file */ }
        }
        // Derive a filename from the URL path; fall back to document.pdf.
        let filename = 'document.pdf';
        try {
            const u = new URL(documentUrl, window.location.origin);
            const last = u.pathname.split('/').pop();
            if (last && /\.pdf$/i.test(last)) filename = last;
            else if (last) filename = `${last}.pdf`;
        } catch (_) { /* noop */ }

        try {
            // Fetch as blob so the browser treats the resulting URL as same-origin
            // and honors the download attribute, regardless of the source's headers.
            const response = await fetch(documentUrl);
            if (!response.ok) throw new Error('fetch failed');
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch (_) {
            // CORS-blocked — fall back to a native <a download> click. Respects
            // Content-Disposition: attachment if the server sends it. If not, the
            // browser will open the PDF in a new tab (best we can do without a
            // backend proxy endpoint).
            const a = document.createElement('a');
            a.href = documentUrl;
            a.download = filename;
            a.rel = 'noopener';
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    }, [documentUrl, form, title]);

    const handlePageInputSubmit = useCallback(
        (e) => {
            e.preventDefault();
            const n = parseInt(pageInput, 10);
            if (Number.isFinite(n) && n >= 1 && n <= numPages) {
                goToPage(n);
            } else {
                setPageInput(String(currentPage));
            }
        },
        [pageInput, numPages, goToPage, currentPage]
    );

    return (
        <div
            ref={rootContainerRef}
            style={{
                // Form fields: focus ring in the sharer's colour (AnnotationLayer.css var).
                '--input-focus-border-color': brandColor || '#2563eb',
                height: '100vh',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: '#333',
            }}
        >
            {/* Top toolbar — three zones (nav / zoom / actions) */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: '#f5f5f5',
                    borderBottom: '1px solid #e0e0e0',
                    padding: '4px 12px',
                    gap: 4,
                    minHeight: 40,
                    flexWrap: 'wrap',
                    flexShrink: 0,
                    zIndex: 2,
                }}
            >
                {brandLogo && (
                    <img src={brandLogo} alt="" style={{ height: 24, maxWidth: 140, objectFit: 'contain', marginRight: 12 }} />
                )}
                {/* Left: Page navigation */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <button
                        type="button"
                        onClick={pagePrev}
                        title="Previous page"
                        aria-label="Previous page"
                        disabled={currentPage <= 1}
                        style={{ ...toolbarBtnStyle, opacity: currentPage <= 1 ? 0.35 : 1 }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>
                    <form onSubmit={handlePageInputSubmit} style={{ display: 'inline-flex', alignItems: 'center', fontSize: 13 }}>
                        <input
                            type="text"
                            value={pageInput}
                            onChange={(e) => setPageInput(e.target.value.replace(/[^0-9]/g, ''))}
                            onBlur={handlePageInputSubmit}
                            style={{
                                width: 36,
                                background: 'white',
                                border: '1px solid #d0d0d0',
                                borderRadius: 4,
                                color: '#222',
                                padding: '4px 6px',
                                textAlign: 'center',
                                fontSize: 13,
                                fontVariantNumeric: 'tabular-nums',
                                outline: 'none',
                            }}
                            aria-label="Current page"
                        />
                        <span style={{ margin: '0 6px', color: '#666' }}>/ {numPages || '—'}</span>
                    </form>
                    <button
                        type="button"
                        onClick={pageNext}
                        title="Next page"
                        aria-label="Next page"
                        disabled={currentPage >= numPages}
                        style={{ ...toolbarBtnStyle, opacity: currentPage >= numPages ? 0.35 : 1 }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </button>
                </div>

                <div style={{ flex: 1 }} />

                {/* Middle: fit + zoom */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <button type="button" onClick={fitToPage} title="Fit page" aria-label="Fit page" style={toolbarBtnStyle}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="5" y="3" width="14" height="18" rx="1" />
                            <path d="M9 7h6M9 12h6M9 17h4" />
                        </svg>
                    </button>
                    <button type="button" onClick={fitToWidth} title="Fit width" aria-label="Fit width" style={toolbarBtnStyle}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 12h18" />
                            <path d="M7 8l-4 4 4 4" />
                            <path d="M17 8l4 4-4 4" />
                        </svg>
                    </button>
                    <div style={{ width: 1, height: 20, background: '#d0d0d0', margin: '0 4px' }} />
                    <button
                        type="button"
                        onClick={() => setScaleImmediate(scaleRef.current / BUTTON_ZOOM_STEP)}
                        title="Zoom out"
                        aria-label="Zoom out"
                        style={toolbarBtnStyle}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="7" />
                            <line x1="21" y1="21" x2="16.5" y2="16.5" />
                            <line x1="8" y1="11" x2="14" y2="11" />
                        </svg>
                    </button>
                    <div style={{ minWidth: 44, textAlign: 'center', fontSize: 13, fontVariantNumeric: 'tabular-nums', color: '#333' }}>
                        {Math.round(displayScale * 100)}%
                    </div>
                    <button
                        type="button"
                        onClick={() => setScaleImmediate(scaleRef.current * BUTTON_ZOOM_STEP)}
                        title="Zoom in"
                        aria-label="Zoom in"
                        style={toolbarBtnStyle}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="7" />
                            <line x1="21" y1="21" x2="16.5" y2="16.5" />
                            <line x1="11" y1="8" x2="11" y2="14" />
                            <line x1="8" y1="11" x2="14" y2="11" />
                        </svg>
                    </button>
                </div>

                <div style={{ flex: 1 }} />

                {/* Right: actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <button
                        type="button"
                        onClick={() => {
                            setSearchOpen((v) => !v);
                            if (!searchOpen) {
                                setTimeout(() => searchInputRef.current?.focus(), 0);
                            }
                        }}
                        title="Search (Cmd+F)"
                        aria-label="Search"
                        style={{
                            ...toolbarBtnStyle,
                            background: searchOpen ? 'rgba(0,0,0,0.08)' : 'transparent',
                        }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                    </button>
                    {isDlEnabled && (
                        <button
                            type="button"
                            onClick={handleDownload}
                            title="Download"
                            aria-label="Download"
                            style={toolbarBtnStyle}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                        </button>
                    )}
                    {isPrintEnabled && (
                        <button
                            type="button"
                            onClick={handlePrint}
                            title="Print"
                            aria-label="Print"
                            style={toolbarBtnStyle}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="6 9 6 2 18 2 18 9" />
                                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                                <rect x="6" y="14" width="12" height="8" />
                            </svg>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={toggleFullscreen}
                        title="Fullscreen"
                        aria-label="Fullscreen"
                        style={toolbarBtnStyle}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                            <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
                            <path d="M3 16v3a2 2 0 0 0 2 2h3" />
                            <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                        </svg>
                    </button>
                    {reportUrl && (
                        <a href={reportUrl} target="_blank" rel="noopener noreferrer" title="Report this content" aria-label="Report this content" style={{ ...toolbarBtnStyle, display: 'inline-flex', color: 'inherit' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 3v1.5M3 21v-6m0 0 2.77-.693a9 9 0 0 1 6.208.682l.108.054a9 9 0 0 0 6.086.71l3.114-.732a48.524 48.524 0 0 1-.005-10.499l-3.11.732a9 9 0 0 1-6.085-.711l-.108-.054a9 9 0 0 0-6.208-.682L3 4.5M3 15V4.5" />
                            </svg>
                        </a>
                    )}
                    {cta && (
                        <a
                            href={cta.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => recordClick({ text: 'CTA: ' + cta.label, href: cta.url })}
                            style={{
                                marginLeft: 8, padding: '6px 12px', borderRadius: 6, background: brandColor || '#2563eb',
                                color: '#fff', fontSize: 13, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap',
                            }}
                        >
                            {cta.label}
                        </a>
                    )}
                </div>
            </div>

            {/* Search bar */}
            {searchOpen && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: '#fafafa',
                        borderBottom: '1px solid #e0e0e0',
                        padding: '6px 12px',
                        gap: 8,
                        flexShrink: 0,
                        zIndex: 2,
                    }}
                >
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        placeholder="Search document…"
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            runSearch(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                if (searchMatches.length === 0) return;
                                if (e.shiftKey) {
                                    goToSearchMatch(
                                        currentMatchIdx <= 0
                                            ? searchMatches.length - 1
                                            : currentMatchIdx - 1
                                    );
                                } else {
                                    goToSearchMatch(
                                        currentMatchIdx >= searchMatches.length - 1
                                            ? 0
                                            : currentMatchIdx + 1
                                    );
                                }
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                closeSearch();
                            }
                        }}
                        style={{
                            flex: '0 1 260px',
                            border: '1px solid #d0d0d0',
                            borderRadius: 6,
                            padding: '6px 10px',
                            fontSize: 13,
                            outline: 'none',
                            background: 'white',
                            color: '#222',
                        }}
                        aria-label="Search text"
                    />
                    <span
                        style={{
                            fontSize: 12,
                            color: '#666',
                            fontVariantNumeric: 'tabular-nums',
                            minWidth: 80,
                        }}
                    >
                        {searchQuery
                            ? searchMatches.length === 0
                                ? 'No matches'
                                : `${currentMatchIdx + 1} of ${searchMatches.length}`
                            : ''}
                    </span>
                    <button
                        type="button"
                        onClick={() =>
                            goToSearchMatch(
                                currentMatchIdx <= 0
                                    ? searchMatches.length - 1
                                    : currentMatchIdx - 1
                            )
                        }
                        title="Previous match (Shift+Enter)"
                        aria-label="Previous match"
                        disabled={searchMatches.length === 0}
                        style={{
                            ...toolbarBtnStyle,
                            opacity: searchMatches.length === 0 ? 0.35 : 1,
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="18 15 12 9 6 15" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={() =>
                            goToSearchMatch(
                                currentMatchIdx >= searchMatches.length - 1
                                    ? 0
                                    : currentMatchIdx + 1
                            )
                        }
                        title="Next match (Enter)"
                        aria-label="Next match"
                        disabled={searchMatches.length === 0}
                        style={{
                            ...toolbarBtnStyle,
                            opacity: searchMatches.length === 0 ? 0.35 : 1,
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                    <div style={{ flex: 1 }} />
                    <button
                        type="button"
                        onClick={closeSearch}
                        title="Close search (Esc)"
                        aria-label="Close search"
                        style={toolbarBtnStyle}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
            )}

            {/* Scrollable PDF area */}
            <div
                ref={scrollContainerRef}
                style={{
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                    overflow: 'auto',
                    background: '#333',
                    touchAction: 'pan-y',
                    position: 'relative',
                }}
            >
            <div
                ref={transformTargetRef}
                style={{
                    display: 'block',
                    padding: '12px 0',
                    // `zoom` is set imperatively; keeping it out of JSX means React
                    // reconciliation never touches it. Start invisible until initial
                    // fit applies so users don't briefly see pages at full RENDER_SCALE.
                    opacity: 0,
                }}
            >
                <Document
                    file={documentUrl}
                    options={PDF_DOCUMENT_OPTIONS}
                    onLoadError={onPdfLoadError(documentUrl)}
                    onLoadSuccess={handleDocumentLoad}
                    onLoadProgress={({ loaded, total }) => {
                        setLoadProgress(total ? { loaded, total } : null);
                    }}
                    loading={
                        <div
                            style={{
                                color: 'white',
                                padding: '48px 20px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 12,
                                fontSize: 14,
                                fontFamily:
                                    '-apple-system, system-ui, sans-serif',
                            }}
                        >
                            <div style={{ color: 'rgba(255,255,255,0.85)' }}>
                                {loadProgress
                                    ? `Loading PDF… ${Math.round(
                                          (loadProgress.loaded /
                                              loadProgress.total) *
                                              100
                                      )}%`
                                    : 'Loading PDF…'}
                            </div>
                            {loadProgress && (
                                <div
                                    style={{
                                        width: 240,
                                        height: 4,
                                        background: 'rgba(255,255,255,0.15)',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: `${Math.min(
                                                100,
                                                (loadProgress.loaded /
                                                    loadProgress.total) *
                                                    100
                                            )}%`,
                                            height: '100%',
                                            background:
                                                'rgba(255,255,255,0.7)',
                                            transition: 'width 120ms linear',
                                        }}
                                    />
                                </div>
                            )}
                            {loadProgress && (
                                <div
                                    style={{
                                        color: 'rgba(255,255,255,0.45)',
                                        fontSize: 12,
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {(
                                        loadProgress.loaded /
                                        (1024 * 1024)
                                    ).toFixed(1)}{' '}
                                    /{' '}
                                    {(
                                        loadProgress.total /
                                        (1024 * 1024)
                                    ).toFixed(1)}{' '}
                                    MB
                                </div>
                            )}
                        </div>
                    }
                    error={
                        <div style={{ color: '#f88', padding: 20 }}>
                            Failed to load PDF
                        </div>
                    }
                >
                    {Array.from({ length: numPages }, (_, i) => {
                        const shouldRender = renderedPages.has(i);
                        const placeholderWidth = firstPageDims
                            ? firstPageDims.width * RENDER_SCALE
                            : undefined;
                        const placeholderHeight = firstPageDims
                            ? firstPageDims.height * RENDER_SCALE
                            : undefined;
                        return (
                            <div
                                key={i}
                                data-page-index={i}
                                style={{
                                    margin: '0 auto 10px',
                                    width: placeholderWidth,
                                    minHeight: placeholderHeight,
                                    // Subtle shimmer shows while the page is still
                                    // rendering or is an un-rendered placeholder.
                                    // The canvas paints on top when ready.
                                    background:
                                        'linear-gradient(90deg, #ffffff 0%, #f2f2f2 50%, #ffffff 100%)',
                                    backgroundSize: '200% 100%',
                                    animation:
                                        'pdfdeck-shimmer 1.6s ease-in-out infinite',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                                }}
                            >
                                {shouldRender ? (
                                    <Page
                                        pageNumber={i + 1}
                                        scale={RENDER_SCALE}
                                        devicePixelRatio={CANVAS_DPR}
                                        renderTextLayer={true}
                                        renderAnnotationLayer={true}
                                        renderForms={Boolean(form)}
                                        externalLinkTarget="_blank"
                                        externalLinkRel="nofollow noopener noreferrer"
                                        onItemClick={({ pageNumber }) =>
                                            goToPage(pageNumber)
                                        }
                                    >
                                        {formsOn && (
                                            <SignatureBoxes
                                                form={form}
                                                pageIndex={i}
                                                scale={RENDER_SCALE}
                                                signed={signed}
                                                brandColor={brandColor}
                                                onOpen={setSigningField}
                                            />
                                        )}
                                        {formsOn && (
                                            <SubmitHotspots
                                                form={form}
                                                pageIndex={i}
                                                scale={RENDER_SCALE}
                                                onSubmit={formSub.submit}
                                            />
                                        )}
                                    </Page>
                                ) : null}
                            </div>
                        );
                    })}
                </Document>
            </div>
            </div>

            {formsOn && pdfProxyRef.current && !form.submitButton && (
                <FormBar form={form} config={formCfg} brandColor={brandColor} sub={formSub} />
            )}
            {formsOn && pdfProxyRef.current && form.submitButton && <FormToast sub={formSub} />}

            {signingField && (
                <Suspense fallback={null}>
                    <SignaturePad
                        open
                        signerName={(signed[signingField.name] && signed[signingField.name].signer_name) || (formConfig && formConfig.signerName) || ''}
                        consentText={(formConfig && formConfig.signConsent) || DEFAULT_SIGN_CONSENT}
                        fieldCount={form && form.signatures ? form.signatures.length : 1}
                        brandColor={brandColor}
                        onClose={() => setSigningField(null)}
                        onApply={({ value, applyAll }) => {
                            setSigned((prev) => {
                                const next = { ...prev, [signingField.name]: value };
                                if (applyAll && form) form.signatures.forEach((f) => { next[f.name] = value; });
                                return next;
                            });
                            setSigningField(null);
                        }}
                    />
                </Suspense>
            )}

            {/*
                "Shared with PDF Deck" branding is rendered by Django's pdf_viewer.html
                template (outside the React root) — not here. Rendering it in both places
                produced a duplicate on preview/update pages.
            */}
        </div>
    );
};

const toolbarBtnStyle = {
    background: 'transparent',
    border: 'none',
    color: '#444',
    cursor: 'pointer',
    padding: '6px 8px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
};

// Inject the shimmer keyframes once globally on first import.
if (typeof document !== 'undefined' && !document.getElementById('pdfdeck-keyframes')) {
    const style = document.createElement('style');
    style.id = 'pdfdeck-keyframes';
    style.textContent = `
        @keyframes pdfdeck-shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
        }
        .annotationLayer section.pdfdeck-field-missing :is(input, textarea, select) {
            outline: 3px solid #dc2626 !important;
            outline-offset: 1px;
        }
        @keyframes pdfdeck-sig-pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.55); }
            50% { box-shadow: 0 0 0 8px rgba(220,38,38,0); }
        }
        .pdfdeck-sig-box.pdfdeck-sig-missing {
            border-color: #dc2626 !important;
            animation: pdfdeck-sig-pulse 1s ease-out 3;
        }
        .pdfdeck-sig-box:focus-visible { outline: 3px solid var(--input-focus-border-color, #2563eb); outline-offset: 2px; }
        .pdfdeck-sig-box.is-signed:hover { background: rgba(37,99,235,0.06) !important; border-color: rgba(37,99,235,0.35) !important; }
    `;
    document.head.appendChild(style);
}

export default PaperReactPdf;
